import {
  Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatChipListbox} from '@angular/material/chips';
import {CmsApiService} from '../../../core/cms-api.service';
import {
  ImportModuleConceptField,
  ImportModuleConceptRow,
  ImportModuleDecisionKind,
  ImportModuleMatchStatus,
} from '../../../core/definitions/import-module';
import {LoggerService} from '../../../core/logger.service';
import {ConceptOption} from '../concept-picker/concept-option.model';
import {
  addCandidate,
  fieldProgress,
  FieldProgress,
  filterCounts,
  hasDetail,
  indexField,
  isRowDecided,
  MATCH_STATUSES,
  occurrencesOf,
  outcomeView,
  pieceViews,
  PieceView,
  ReviewFilter,
  ReviewSort,
  reviewTotals,
  ReviewTotals,
  runOutcome,
  RunOutcomeView,
  selectCandidate,
  setRowDecision,
  statusCount,
  termTrailLabel,
  visibleRows,
} from './value-review.model';

/** Rows rendered before "show all", so a field with hundreds of values
 * does not render them all at once. */
export const ROW_PAGE_SIZE = 100;

/** Pause after the last keystroke in the search before the rows are
 * filtered again. */
export const QUERY_DEBOUNCE_MS = 150;

const STATUS_KEYS: Record<ImportModuleMatchStatus, string> = {
  ambiguous: 'TRANS__IMPORT__VALUE_REVIEW__STATUS_AMBIGUOUS',
  no_match: 'TRANS__IMPORT__VALUE_REVIEW__STATUS_NO_MATCH',
  composite: 'TRANS__IMPORT__VALUE_REVIEW__STATUS_COMPOSITE',
  struck_only: 'TRANS__IMPORT__VALUE_REVIEW__STATUS_STRUCK_ONLY',
};

const FILTER_KEYS: Record<ReviewFilter, string> = {
  all: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_ALL',
  undecided: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_UNDECIDED',
  ambiguous: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_AMBIGUOUS',
  no_match: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_NO_MATCH',
  composite: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_COMPOSITE',
  struck_only: 'TRANS__IMPORT__VALUE_REVIEW__FILTER_STRUCK_ONLY',
};

// Shared empty lists, so a binding reads the same array on every check.
const NO_OPTIONS: ConceptOption[] = [];
const NO_PIECES: PieceView[] = [];

/**
 * The Import module's value review: every concept value the whole-file
 * scan could not resolve to exactly one term, per target field, each with
 * a decision - pick a term (`Velg`), create one (`Opprett`), split the
 * value (`Del opp`) or skip it (`Hopp over`) - and what the run does with
 * the value as its decision stands.
 *
 * The host owns `fields`: this component edits their rows in place and
 * emits `decisionsChanged` after each edit. It asks the host to `rescan`.
 * It loads a hierarchic field's terms itself, for the create-under parent
 * picker. The page around it gives the step its title and intro.
 */
@Component({
  selector: 'app-value-review-page',
  templateUrl: './value-review-page.component.html',
  styleUrls: ['./value-review-page.component.scss'],
  standalone: false,
})
export class ValueReviewPageComponent implements OnChanges, OnDestroy {
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  @Input() fields: ImportModuleConceptField[] = [];
  @Input() loading = false;
  @Input() rowsScanned = 0;
  /** The superobject type id being imported - a list filtered by object
   * type offers only the terms valid for it. */
  @Input({required: true}) objectType!: string;
  /** A target field's display label (the backend refers to fields by id). */
  @Input() resolveLabel: (fieldId: string) => string = fieldId => fieldId;
  /** The source columns mapped onto a target field. */
  @Input() resolveColumns: (fieldId: string) => string[] = () => [];

  /** A row's decision, picked term, or new term was edited. */
  @Output() decisionsChanged = new EventEmitter<void>();
  @Output() rescan = new EventEmitter<void>();

  @ViewChild('filterList') filterList?: MatChipListbox;

  readonly statuses = MATCH_STATUSES;
  readonly statusKeys = STATUS_KEYS;
  readonly filterKeys = FILTER_KEYS;
  readonly statusCount = statusCount;
  readonly hasDetail = hasDetail;

  selectedFieldId: string | null = null;
  filter: ReviewFilter = 'all';
  query = '';
  sort: ReviewSort = 'occurrences';
  rowLimit = ROW_PAGE_SIZE;
  readonly expanded = new Set<ImportModuleConceptRow>();
  readonly selected = new Set<ImportModuleConceptRow>();
  bulkMode: 'pick' | 'create' | null = null;
  readonly bulkParentControl = new FormControl<string | null>(null);

  // Derived from `fields` and the decisions by `refreshView`, so the
  // template reads stable values instead of recomputing per change check.
  progressByField = new Map<string, FieldProgress>();
  totals: ReviewTotals = {values: 0, fields: 0, occurrences: 0, decided: 0, undecided: 0};
  counts: Record<ReviewFilter, number> = {all: 0, undecided: 0, ambiguous: 0, no_match: 0, composite: 0, struck_only: 0};
  filteredRows: ImportModuleConceptRow[] = [];
  shownRows: ImportModuleConceptRow[] = [];
  maxOccurrences = 0;
  outcomeViews = new Map<ImportModuleConceptRow, RunOutcomeView>();
  occurrences = new Map<ImportModuleConceptRow, number>();
  pieceViewsByRow = new Map<ImportModuleConceptRow, PieceView[]>();
  rowIds = new Map<ImportModuleConceptRow, string>();

  private readonly parentOptionsByField: Record<string, ConceptOption[]> = {};
  private readonly parentOptionsRequested = new Set<string>();
  private queryTimer: ReturnType<typeof setTimeout> | null = null;
  /** The row `jumpToRow` last showed: the shown rows keep reaching down to
   * it, wherever a later refresh sorts it, until the field, filter or
   * search changes. */
  private jumpTarget: ImportModuleConceptRow | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['objectType'] && !changes['objectType'].firstChange) {
      // A list filtered by object type offers other terms for another type.
      Object.keys(this.parentOptionsByField).forEach(key => delete this.parentOptionsByField[key]);
      this.parentOptionsRequested.clear();
      this.loadParentOptions(this.selectedField);
    }
    if (changes['fields']) {
      if (!this.fields.some(f => f.target_field === this.selectedFieldId)) {
        const firstReviewed = this.fields.find(f => f.review_rows.length);
        this.selectedFieldId = (firstReviewed ?? this.fields[0])?.target_field ?? null;
      }
      this.expanded.clear();
      this.selected.clear();
      this.jumpTarget = null;
      this.loadParentOptions(this.selectedField);
      this.progressByField = new Map(this.fields.map(f => [f.target_field, fieldProgress(f)]));
      this.totals = reviewTotals([...this.progressByField.values()]);
      this.refreshView();
    }
  }

  ngOnDestroy(): void {
    this.cancelQueryRefresh();
  }

  get selectedField(): ImportModuleConceptField | null {
    return this.fields.find(f => f.target_field === this.selectedFieldId) ?? null;
  }

  get parentOptions(): ConceptOption[] {
    return this.parentOptionsByField[this.selectedFieldId ?? ''] ?? NO_OPTIONS;
  }

  piecesOf(row: ImportModuleConceptRow): PieceView[] {
    return this.pieceViewsByRow.get(row) ?? NO_PIECES;
  }

  get selectedOccurrences(): number {
    return [...this.selected].reduce((sum, row) => sum + row.occurrence_count, 0);
  }

  get allShownSelected(): boolean {
    return this.filteredRows.length > 0 && this.filteredRows.every(row => this.selected.has(row));
  }

  get someShownSelected(): boolean {
    return this.filteredRows.some(row => this.selected.has(row)) && !this.allShownSelected;
  }

  selectField(field: ImportModuleConceptField): void {
    if (field.target_field === this.selectedFieldId) {
      return;
    }
    this.cancelQueryRefresh();
    this.jumpTarget = null;
    this.selectedFieldId = field.target_field;
    this.filter = 'all';
    this.query = '';
    this.rowLimit = ROW_PAGE_SIZE;
    this.expanded.clear();
    this.clearSelection();
    this.loadParentOptions(field);
    this.refreshView();
  }

  setFilter(value: ReviewFilter | undefined): void {
    this.cancelQueryRefresh();
    this.jumpTarget = null;
    // Clicking the active chip deselects it; the filter stays where it is.
    this.filter = value ?? this.filter;
    if (value === undefined && this.filterList) {
      this.filterList.value = this.filter;
    }
    this.rowLimit = ROW_PAGE_SIZE;
    this.refreshView();
  }

  /** The search box's text. The rows are filtered by it once the typing
   * pauses for `QUERY_DEBOUNCE_MS`. */
  setQuery(query: string): void {
    this.query = query;
    this.cancelQueryRefresh();
    this.queryTimer = setTimeout(() => this.applyPendingQuery(), QUERY_DEBOUNCE_MS);
  }

  setSort(sort: ReviewSort): void {
    this.applyPendingQuery();
    this.sort = sort;
    this.refreshView();
  }

  showAllRows(): void {
    this.applyPendingQuery();
    this.rowLimit = Infinity;
    this.refreshView();
  }

  /**
   * `row`'s new decision, expanded when it has a detail. The first switch
   * to `Opprett` names the new term after the value and places it where
   * the field's group card creates missing terms.
   */
  setDecision(row: ImportModuleConceptRow, kind: ImportModuleDecisionKind): void {
    if (kind === 'create_new' && !row.new_concept_name?.trim()) {
      row.new_concept_name = row.source_value;
      row.new_concept_parent_id = this.selectedField?.create_missing?.parent_id ?? null;
    }
    setRowDecision(row, kind);
    if (hasDetail(row)) {
      this.expanded.add(row);
    } else {
      this.expanded.delete(row);
    }
    this.onDecisionsEdited();
  }

  canSplit(row: ImportModuleConceptRow): boolean {
    return row.match_status === 'composite' && !!this.selectedField?.split_delimiter;
  }

  toggleExpanded(row: ImportModuleConceptRow): void {
    if (this.expanded.has(row)) {
      this.expanded.delete(row);
    } else {
      this.expanded.add(row);
    }
  }

  isExpanded(row: ImportModuleConceptRow): boolean {
    return this.expanded.has(row) && hasDetail(row);
  }

  onDecisionsEdited(): void {
    this.applyPendingQuery();
    this.refreshView(true);
    this.decisionsChanged.emit();
  }

  /** Shows `row` - a piece's own row - expanded, clearing whatever hides
   * it, and scrolls to it. The shown rows reach down to `row`, not
   * further. */
  jumpToRow(row: ImportModuleConceptRow): void {
    this.cancelQueryRefresh();
    this.filter = 'all';
    this.query = '';
    this.jumpTarget = row;
    if (hasDetail(row)) {
      this.expanded.add(row);
    }
    this.refreshView();
    const id = this.rowIds.get(row);
    setTimeout(() => document.getElementById(id ?? '')?.scrollIntoView?.({block: 'center', behavior: 'smooth'}));
  }

  skipAllUndecided(): void {
    for (const row of this.selectedField?.review_rows ?? []) {
      if (!isRowDecided(row)) {
        setRowDecision(row, 'skip');
      }
    }
    this.onDecisionsEdited();
  }

  // --- selection and bulk actions ---

  toggleRowSelected(row: ImportModuleConceptRow, checked: boolean): void {
    if (checked) {
      this.selected.add(row);
    } else {
      this.selected.delete(row);
    }
  }

  toggleAllSelected(checked: boolean): void {
    this.applyPendingQuery();
    for (const row of this.filteredRows) {
      this.toggleRowSelected(row, checked);
    }
  }

  clearSelection(): void {
    this.selected.clear();
    this.bulkMode = null;
  }

  bulkSkip(): void {
    for (const row of this.selected) {
      setRowDecision(row, 'skip');
      this.expanded.delete(row);
    }
    this.finishBulk();
  }

  /** The selected values `bulkSplit` splits: those `canSplit` allows. */
  get splittableSelected(): ImportModuleConceptRow[] {
    return [...this.selected].filter(row => this.canSplit(row));
  }

  /** Every selected composite value is split; any other selected value
   * keeps its decision. */
  bulkSplit(): void {
    for (const row of this.splittableSelected) {
      setRowDecision(row, 'split');
    }
    this.finishBulk();
  }

  bulkPick(option: ConceptOption): void {
    for (const row of this.selected) {
      addCandidate(row, option);
      selectCandidate(row, option.id);
      setRowDecision(row, 'pick_existing');
    }
    this.finishBulk();
  }

  /** Every selected value becomes a new term named after it, under the
   * bulk bar's parent (a root term when none, or for a flat list). */
  bulkCreate(): void {
    const parentId = this.selectedField?.hierarchic ? this.bulkParentControl.value : null;
    for (const row of this.selected) {
      setRowDecision(row, 'create_new');
      row.new_concept_name = row.new_concept_name?.trim() || row.source_value;
      row.new_concept_parent_id = parentId;
    }
    this.finishBulk();
  }

  private finishBulk(): void {
    this.clearSelection();
    this.bulkParentControl.setValue(null);
    this.onDecisionsEdited();
  }

  // --- derived state ---

  /** Filters by a search still waiting out its pause, now: an action on
   * the rows acts on the rows the typed search shows, and the pause ending
   * later does not undo it. */
  private applyPendingQuery(): void {
    if (this.queryTimer === null) {
      return;
    }
    this.cancelQueryRefresh();
    this.jumpTarget = null;
    this.rowLimit = ROW_PAGE_SIZE;
    this.refreshView();
  }

  private cancelQueryRefresh(): void {
    if (this.queryTimer !== null) {
      clearTimeout(this.queryTimer);
      this.queryTimer = null;
    }
  }

  /**
   * Derives the selected field's rows, counts and outcomes. `recount` also
   * recounts that field's progress and the totals, after an edit: an edit
   * touches only the selected field's rows, and a new `fields` recounts
   * every field in `ngOnChanges`.
   */
  private refreshView(recount = false): void {
    const field = this.selectedField;
    if (!field) {
      this.filteredRows = [];
      this.shownRows = [];
      return;
    }
    const index = indexField(field);
    if (recount) {
      this.progressByField.set(field.target_field, fieldProgress(field, index));
      this.totals = reviewTotals([...this.progressByField.values()]);
    }
    const parentLabel = termTrailLabel(this.parentOptions);
    this.counts = filterCounts(field);
    this.filteredRows = visibleRows(field, this.filter, this.query, this.sort, index);
    if (this.jumpTarget) {
      this.rowLimit = Math.max(this.rowLimit, this.filteredRows.indexOf(this.jumpTarget) + 1);
    }
    this.shownRows = this.filteredRows.slice(0, this.rowLimit);
    this.occurrences = new Map(field.review_rows.map(row => [row, occurrencesOf(row, field, index)]));
    this.maxOccurrences = Math.max(0, ...this.occurrences.values());
    this.outcomeViews = new Map(field.review_rows.map(row =>
      [row, outcomeView(runOutcome(row, field, index), parentLabel)]));
    this.pieceViewsByRow = new Map(field.review_rows
      .filter(row => row.pieces?.length)
      .map(row => [row, pieceViews(row, field, parentLabel, index)]));
    const fieldIndex = this.fields.indexOf(field);
    this.rowIds = new Map(field.review_rows.map((row, i) => [row, `value-review-row-${fieldIndex}-${i}`]));
  }

  private loadParentOptions(field: ImportModuleConceptField | null): void {
    if (!field?.hierarchic || !this.objectType || this.parentOptionsRequested.has(field.target_field)) {
      return;
    }
    this.parentOptionsRequested.add(field.target_field);
    this.cms.importModuleConceptTerms({field_id: field.target_field, object_type: this.objectType})
      .then(res => {
        this.parentOptionsByField[field.target_field] = res.terms;
        this.refreshView();
      })
      .catch(e => {
        this.parentOptionsRequested.delete(field.target_field);
        this.logger.error(`value review: could not load the terms of ${field.target_field}: ${e}`);
      });
  }
}
